import React, { useEffect, useState } from 'react';
import { Modal, Select, Input, Button, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import { fetchBookingFormData, fetchSiteShiftTypes, fetchSiteWorkTypes, generateBookingLink } from 'core/bookingLinks/bookingLinksApi';
import { clearSiteShiftTypes, clearSiteWorkTypes } from 'core/bookingLinks/bookingLinksSlice';
import WorkTypeSelector from 'components/Form/WorkTypeSelector';
import { CheckboxData } from 'components/Form/NestedCheckboxSearch/types';
import { ChevronDownIcon } from 'components/Elements/CustomSVGIcon';
import { validateEmail } from 'core/utils/helpers';
import { REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING } from 'core/utils/value';
import siteOptionLabel from './siteOptionLabel';
import './generateBookingLinkModal.scss';

interface GenerateBookingLinkModalProps {
    open: boolean;
    onClose: () => void;
}

// What a non-visitor site can be booked for. The values are the API's request
// types; the labels match the site's own booking menu wording.
const REQUEST_TYPE_OPTIONS = [
    { value: REQUEST_TYPE_ACCESS, label: 'General Access' },
    { value: REQUEST_TYPE_GUARDING, label: 'Guarding' },
];

const GenerateBookingLinkModal: React.FC<GenerateBookingLinkModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { sites, siteWorkTypes, siteShiftTypes, isGenerating } = useSelector(
        (state: CombinedStates) => state.bookingLinks,
    );

    const [siteId, setSiteId] = useState<number | null>(null);
    const [workTypeData, setWorkTypeData] = useState<CheckboxData[]>([]);
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [allowedShiftTypes, setAllowedShiftTypes] = useState<number[]>([]);
    const [requestType, setRequestType] = useState<string | null>(null);

    // Only a site that isn't a visitor-request site has a choice to make.
    const selectedSite = sites.find((site) => site.id === siteId);
    const requiresRequestType = Boolean(selectedSite) && !selectedSite?.isVisitorRequest;

    useEffect(() => {
        if (open) {
            dispatch(fetchBookingFormData());
        }
    }, [open, dispatch]);

    // Rebuild the selector data (as CheckboxData) whenever the chosen site's work
    // types load — same transform as the normal work-type step.
    useEffect(() => {
        setWorkTypeData(siteWorkTypes.map((workType) => ({
            ...workType,
            value: workType.id.toString(),
            checked: false,
            children: (workType.children || []).map((child) => ({
                ...child,
                value: child.id.toString(),
                checked: false,
            })),
        })) as CheckboxData[]);
    }, [siteWorkTypes]);

    const resetForm = () => {
        setSiteId(null);
        setWorkTypeData([]);
        setName('');
        setEmail('');
        setAllowedShiftTypes([]);
        setRequestType(null);
        dispatch(clearSiteWorkTypes());
        dispatch(clearSiteShiftTypes());
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSiteChange = (value: number) => {
        setSiteId(value);
        setAllowedShiftTypes([]);
        // The previous site's answer says nothing about this one.
        setRequestType(null);
        dispatch(clearSiteWorkTypes());
        dispatch(clearSiteShiftTypes());
        dispatch(fetchSiteWorkTypes(value));
        dispatch(fetchSiteShiftTypes(value));
    };

    const getSelectedWorkTypes = () => {
        const parent = workTypeData.find((workType) => workType.checked);
        const child = parent?.children?.find((childType) => childType.checked);
        return { parent, child };
    };

    const handleSubmit = async () => {
        const { parent, child } = getSelectedWorkTypes();

        if (!siteId || !parent || !child || !name.trim() || !email.trim()) {
            notification.error({
                message: 'Missing details',
                description: 'Please choose a site, a parent and child work type, and enter the guest name and email.',
            });
            return;
        }

        // The API refuses this too — asking here means the admin finds out
        // before the guest would have been emailed the wrong kind of form.
        if (requiresRequestType && !requestType) {
            notification.error({
                message: 'Request type not chosen',
                description: 'This site is not a visitor-request site. Please choose whether the guest should make a General Access or Guarding request.',
            });
            return;
        }

        if (!validateEmail(email)) {
            notification.error({
                message: 'Invalid email',
                description: 'Please enter a valid guest email address (e.g. guest@example.com).',
            });
            return;
        }

        const workTypeIds = [parent.id, child.id].filter((value): value is number => value !== undefined);

        const result = await dispatch(generateBookingLink({
            site_id: siteId,
            name: name.trim(),
            email: email.trim(),
            work_type_ids: workTypeIds,
            ...(requiresRequestType && requestType ? { request_type: requestType } : {}),
            // Only send the restriction when the admin picked one; empty means
            // no restriction (guest may book any of the site's shifts).
            ...(allowedShiftTypes.length > 0 ? { allowed_shift_types: allowedShiftTypes } : {}),
        }));

        if (generateBookingLink.fulfilled.match(result)) {
            handleClose();
        }
    };

    return (
        <Modal
            open={open}
            centered
            width={640}
            footer={null}
            onCancel={handleClose}
            title="Generate one-off booking link"
            wrapClassName="generate-booking-link-modal">
            <div className="generate-booking-link-modal__inner">
                <p className="field-label">Site</p>
                <Select
                    className="booking-site-select"
                    placeholder="Choose a site"
                    value={(siteId ?? undefined) as number}
                    onChange={handleSiteChange}
                    options={sites.map((site) => ({ value: site.id, label: siteOptionLabel(site) }))}
                    suffixIcon={<ChevronDownIcon />}
                    showSearch
                    optionFilterProp="label" />

                {/* A visitor-request site raises one kind of request, so it isn't
                    asked. Any other site supports both General Access and
                    Guarding, and the guest can't choose for themselves — so the
                    admin has to, and the link isn't sent until they do. */}
                {siteId && requiresRequestType && (
                    <>
                        <p className="field-label">Request type</p>
                        <Select
                            className="booking-site-select"
                            placeholder="Choose the request the guest should make"
                            value={(requestType ?? undefined) as string}
                            onChange={(value: string) => setRequestType(value)}
                            options={REQUEST_TYPE_OPTIONS}
                            suffixIcon={<ChevronDownIcon />} />
                    </>
                )}

                {siteId && (
                    <WorkTypeSelector workTypeData={workTypeData} handleChange={setWorkTypeData} />
                )}

                {siteId && siteShiftTypes.length > 0 && (
                    <>
                        <p className="field-label">Restrict to shift types (optional)</p>
                        <Select
                            className="booking-site-select"
                            mode="multiple"
                            allowClear
                            placeholder="Any shift (no restriction)"
                            value={allowedShiftTypes}
                            onChange={(values: number[]) => setAllowedShiftTypes(values)}
                            options={siteShiftTypes}
                            suffixIcon={<ChevronDownIcon />}
                            optionFilterProp="label" />
                    </>
                )}

                <p className="field-label">Guest name</p>
                <Input placeholder="Guest name" value={name} onChange={(e) => setName(e.target.value)} />

                <p className="field-label">Guest email</p>
                <Input
                    placeholder="guest@example.com"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)} />

                <div className="generate-booking-link-modal__actions">
                    <Button
                        type="primary"
                        ghost
                        className="form-button cancel-button"
                        onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        className="form-button"
                        loading={isGenerating}
                        onClick={handleSubmit}>
                        Send link
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default GenerateBookingLinkModal;
